/**
 * Planner Insights — feature-local data hooks + client-side aggregator.
 *
 * Wave-8 ships insights as a *derived* view over the existing `/Planner/cards`
 * endpoint plus the existing `/TenantLocation` list endpoint. When the backend
 * exposes a dedicated `/Planner/Insights` endpoint, only the
 * `fetchPlannerInsights` function changes — the consumer-facing
 * `usePlannerInsights` and `useInsightsTenantLocations` hooks stay stable.
 *
 * NOTE: this file is owned by Agent C (Insights). Agent A owns the rest of
 * `features/planner/api/*` (queries, mutations, client, keys). Keep helpers
 * here self-contained so a future refactor that moves them into Agent A's
 * `client.ts` is a copy-paste, not a merge.
 */
import { useQuery } from '@tanstack/react-query';

import { APIClient } from '@/helpers/api_helper';
import {
  GET_PLANNER_CARDS,
  GET_TENANT_LOCATIONS,
} from '@/helpers/url_helper';
import { PlannerActivityStatus } from '@/features/dispatch-board/planner-data/plannerTypes';

import type {
  InsightsCompletionPoint,
  InsightsFilters,
  InsightsKpiSummary,
  InsightsStatusKey,
  InsightsStatusSlice,
  InsightsTenantLocationOption,
  InsightsTopClient,
  InsightsUtilizationPoint,
  PlannerCardDto,
  PlannerInsightsPayload,
} from '../types/insights';

const api = new APIClient();

/* -------------------------------------------------------------------------- */
/* Query keys                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Local key namespace so insights cache invalidation never collides with
 * Agent A's `plannerKeys` factory.
 */
export const plannerInsightsKeys = {
  all: ['planner-insights'] as const,
  payload: (filters: InsightsFilters) =>
    [...plannerInsightsKeys.all, 'payload', filters] as const,
  tenantLocations: () =>
    [...plannerInsightsKeys.all, 'tenant-locations'] as const,
};

/* -------------------------------------------------------------------------- */
/* HTTP                                                                       */
/* -------------------------------------------------------------------------- */

interface RawTenantLocation {
  id: string;
  name: string;
  isDeleted?: boolean;
}

interface TenantLocationsResponse {
  items?: RawTenantLocation[];
}

/** Convert anything axios-y into a plain array. */
function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.items ?? obj.result ?? obj.data;
    if (Array.isArray(arr)) return arr as T[];
  }
  return [];
}

async function fetchPlannerCards(
  startDate: string,
  endDate: string,
): Promise<PlannerCardDto[]> {
  const response = await api.get<unknown>(GET_PLANNER_CARDS, {
    from: startDate,
    to: endDate,
  });
  return toArray<PlannerCardDto>(response);
}

async function fetchTenantLocations(): Promise<InsightsTenantLocationOption[]> {
  const response = await api.get<TenantLocationsResponse>(
    GET_TENANT_LOCATIONS,
    { pageNumber: 1, pageSize: 200 },
  );
  const items = response?.items ?? [];
  return items
    .filter((loc) => !loc.isDeleted)
    .map((loc) => ({ id: loc.id, name: loc.name }));
}

/* -------------------------------------------------------------------------- */
/* Date helpers                                                               */
/* -------------------------------------------------------------------------- */

/** `yyyy-MM-dd` slice of an ISO datetime (treats absent values as ''). */
function isoDateKey(iso: string | undefined): string {
  if (!iso) return '';
  // Defensive — covers both `2026-05-12T08:00:00Z` and `2026-05-12`.
  return iso.slice(0, 10);
}

function shortLabel(dateKey: string): string {
  if (!dateKey) return '';
  const [, m, d] = dateKey.split('-');
  if (!m || !d) return dateKey;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const mIdx = parseInt(m, 10) - 1;
  if (Number.isNaN(mIdx) || mIdx < 0 || mIdx > 11) return dateKey;
  return `${months[mIdx]} ${parseInt(d, 10)}`;
}

/** Iterate every yyyy-MM-dd between `start` and `end` inclusive. */
function eachDay(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return out;
  }
  for (
    const cursor = new Date(start);
    cursor.getTime() <= end.getTime();
    cursor.setDate(cursor.getDate() + 1)
  ) {
    out.push(cursor.toISOString().slice(0, 10));
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Status keying                                                              */
/* -------------------------------------------------------------------------- */

const STATUS_VALUE_TO_KEY: Record<number, InsightsStatusKey> = {
  [PlannerActivityStatus.Scheduled]: 'Scheduled',
  [PlannerActivityStatus.InProgress]: 'InProgress',
  [PlannerActivityStatus.CompletedPendingReview]: 'CompletedPendingReview',
  [PlannerActivityStatus.Approved]: 'Approved',
  [PlannerActivityStatus.Rejected]: 'Rejected',
  [PlannerActivityStatus.Cancelled]: 'Cancelled',
};

const STATUS_LABELS: Record<InsightsStatusKey, string> = {
  Scheduled: 'Scheduled',
  InProgress: 'In progress',
  CompletedPendingReview: 'Pending review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
};

/**
 * Recharts SVG accepts `hsl(var(--*))` directly so we use the brand tokens.
 * Falling back to a fixed colour for slices that don't have a natural token.
 */
const STATUS_FILLS: Record<InsightsStatusKey, string> = {
  Scheduled: 'hsl(var(--primary))',
  InProgress: 'hsl(var(--warning))',
  CompletedPendingReview: 'hsl(var(--secondary))',
  Approved: 'hsl(var(--success))',
  Rejected: 'hsl(var(--destructive))',
  Cancelled: 'hsl(var(--muted-foreground))',
};

const COMPLETED_STATUSES = new Set<InsightsStatusKey>([
  'Approved',
  'CompletedPendingReview',
]);

function statusKeyFor(card: PlannerCardDto): InsightsStatusKey | undefined {
  return STATUS_VALUE_TO_KEY[card.status];
}

/* -------------------------------------------------------------------------- */
/* Aggregation                                                                */
/* -------------------------------------------------------------------------- */

interface AggregateOptions {
  filters: InsightsFilters;
  cards: PlannerCardDto[];
  priorCards?: PlannerCardDto[];
}

/**
 * Pure aggregator — testable in isolation. Given a window of planner cards
 * (and optionally a comparable prior window), returns the page-level payload.
 */
export function aggregateInsights({
  filters,
  cards,
  priorCards,
}: AggregateOptions): PlannerInsightsPayload {
  const dayKeys = eachDay(filters.startDate, filters.endDate);
  const dayIndex = new Map<string, number>();
  dayKeys.forEach((d, idx) => dayIndex.set(d, idx));

  // KPI counters --------------------------------------------------------------
  let totalActivities = 0;
  const statusCounts: Record<InsightsStatusKey, number> = {
    Scheduled: 0,
    InProgress: 0,
    CompletedPendingReview: 0,
    Approved: 0,
    Rejected: 0,
    Cancelled: 0,
  };
  const crewIds = new Set<string>();
  const clientNames = new Set<string>();
  let crewAssignmentsTotal = 0;

  // Utilization / completion arrays seeded with every day in range -----------
  const utilization: InsightsUtilizationPoint[] = dayKeys.map((d) => ({
    date: d,
    label: shortLabel(d),
    crewCount: 0,
    activityCount: 0,
  }));
  const completion: InsightsCompletionPoint[] = dayKeys.map((d) => ({
    date: d,
    label: shortLabel(d),
    planned: 0,
    completed: 0,
  }));

  // Top clients accumulator ---------------------------------------------------
  type ClientAcc = {
    clientName: string;
    activityCount: number;
    completedCount: number;
    crewAssignments: number;
    priorityCustomer: boolean;
  };
  const clientByName = new Map<string, ClientAcc>();

  for (const card of cards) {
    const key = statusKeyFor(card);
    if (key) statusCounts[key] += 1;
    totalActivities += 1;

    const crewCount = typeof card.crewCount === 'number' ? card.crewCount : 0;
    crewAssignmentsTotal += crewCount;

    // Bucket by scheduled-start date -- card may pre-date the filter when
    // the backend returns boundary days; clamp via dayIndex membership.
    const dateKey = isoDateKey(card.scheduledStart);
    const idx = dayIndex.get(dateKey);
    if (idx !== undefined) {
      utilization[idx].activityCount += 1;
      utilization[idx].crewCount += crewCount;
      completion[idx].planned += 1;
      if (key && COMPLETED_STATUSES.has(key)) {
        completion[idx].completed += 1;
      }
    }

    // Unique crew — the cards endpoint exposes supervisorUserId only, so the
    // best proxy for "unique crew touchpoints" is supervisors-plus-counts.
    // We approximate by counting non-empty supervisor ids; this is the same
    // dimension that drives the executive view in dashboards/.
    if (card.supervisorUserId) crewIds.add(card.supervisorUserId);

    // Client accumulator.
    const clientName = (card.clientName ?? '').trim();
    if (clientName) {
      clientNames.add(clientName);
      const acc = clientByName.get(clientName) ?? {
        clientName,
        activityCount: 0,
        completedCount: 0,
        crewAssignments: 0,
        priorityCustomer: false,
      };
      acc.activityCount += 1;
      acc.crewAssignments += crewCount;
      if (key && COMPLETED_STATUSES.has(key)) acc.completedCount += 1;
      if (card.priorityCustomer) acc.priorityCustomer = true;
      clientByName.set(clientName, acc);
    }
  }

  // Status pie slices ---------------------------------------------------------
  const statusBreakdown: InsightsStatusSlice[] = (
    Object.keys(statusCounts) as InsightsStatusKey[]
  ).map((k) => ({
    key: k,
    label: STATUS_LABELS[k],
    value: statusCounts[k],
    fill: STATUS_FILLS[k],
  }));

  // Top clients (sorted by activity count, top 8) -----------------------------
  const topClients: InsightsTopClient[] = Array.from(clientByName.values())
    .sort((a, b) => b.activityCount - a.activityCount || a.clientName.localeCompare(b.clientName))
    .slice(0, 8);

  // KPI summary ---------------------------------------------------------------
  const dayCount = Math.max(1, dayKeys.length);
  const completedActivities =
    statusCounts.Approved + statusCounts.CompletedPendingReview;

  const kpis: InsightsKpiSummary = {
    totalActivities,
    scheduledActivities: statusCounts.Scheduled,
    inProgressActivities: statusCounts.InProgress,
    completedActivities,
    cancelledActivities: statusCounts.Cancelled,
    uniqueCrew: crewIds.size,
    uniqueClients: clientNames.size,
    averageActivitiesPerDay: Number(
      (totalActivities / dayCount).toFixed(1),
    ),
  };

  if (priorCards && priorCards.length > 0) {
    const priorCrewIds = new Set<string>();
    const priorClients = new Set<string>();
    let priorTotal = 0;
    let priorCompleted = 0;
    for (const card of priorCards) {
      priorTotal += 1;
      const key = statusKeyFor(card);
      if (key && COMPLETED_STATUSES.has(key)) priorCompleted += 1;
      if (card.supervisorUserId) priorCrewIds.add(card.supervisorUserId);
      if (card.clientName) priorClients.add(card.clientName.trim());
    }
    kpis.prior = {
      totalActivities: priorTotal,
      completedActivities: priorCompleted,
      uniqueCrew: priorCrewIds.size,
      uniqueClients: priorClients.size,
    };
  }

  // Tenant-location filter is applied to `cards` upstream (in the query
  // function) by matching the card's site address; we surface it on the
  // payload for traceability.
  void crewAssignmentsTotal;

  return {
    filters,
    kpis,
    utilization,
    statusBreakdown,
    completion,
    topClients,
  };
}

/* -------------------------------------------------------------------------- */
/* Prior-period helper                                                        */
/* -------------------------------------------------------------------------- */

function priorRange(filters: InsightsFilters): {
  start: string;
  end: string;
} | null {
  const start = new Date(`${filters.startDate}T00:00:00`);
  const end = new Date(`${filters.endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (days <= 0) return null;
  const priorEnd = new Date(start);
  priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - (days - 1));
  return {
    start: priorStart.toISOString().slice(0, 10),
    end: priorEnd.toISOString().slice(0, 10),
  };
}

/* -------------------------------------------------------------------------- */
/* Top-level fetcher                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Pulls the cards for the current window (and an equivalent prior window for
 * delta chips), then aggregates the result client-side.
 *
 * Filtering by `tenantLocationId` is best-effort against the card's site
 * address — the cards endpoint doesn't yet ship a tenant-location FK so we
 * surface this as a soft filter. When the backend ships a stronger linkage
 * (Wave 9?), this block changes; the rest of the file stays intact.
 */
export async function fetchPlannerInsights(
  filters: InsightsFilters,
): Promise<PlannerInsightsPayload> {
  const [cards, priorCards] = await Promise.all([
    fetchPlannerCards(filters.startDate, filters.endDate),
    (async () => {
      const range = priorRange(filters);
      if (!range) return [] as PlannerCardDto[];
      try {
        return await fetchPlannerCards(range.start, range.end);
      } catch {
        // Prior-period failures are non-fatal — just drop the deltas.
        return [] as PlannerCardDto[];
      }
    })(),
  ]);
  return aggregateInsights({ filters, cards, priorCards });
}

/* -------------------------------------------------------------------------- */
/* Hooks                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `usePlannerInsights` — page-level data hook used by `PlannerInsightsPage`.
 *
 * Disabled until both endpoints of the filter window are valid `yyyy-MM-dd`
 * strings so a half-resolved filter doesn't fire a request.
 */
export function usePlannerInsights(filters: InsightsFilters) {
  const enabled = Boolean(filters.startDate && filters.endDate);
  return useQuery({
    queryKey: plannerInsightsKeys.payload(filters),
    queryFn: () => fetchPlannerInsights(filters),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Tenant location list for the filter dropdown — cached globally so multiple
 * insights mounts share the same fetch.
 */
export function useInsightsTenantLocations() {
  return useQuery({
    queryKey: plannerInsightsKeys.tenantLocations(),
    queryFn: fetchTenantLocations,
    staleTime: 5 * 60_000,
  });
}
